// Copyright (c) Docugami, Inc. All rights reserved.**/

import { WorkPathType } from "networking/WorkPathApi";
import { NavigationType, ReportViewMode, useNavigationService } from "./navigationService";
import { dgIconNames } from "./dgIcons";
import { getIconForReportViewMode, getNameForReportViewMode } from "components/reports/ReportsHelpers";
import { DocumentEvaluationStatus } from "networking/reportsApi";
import { useFeatureFlag } from "./FeatureFlags";
import { FeatureFlags } from "store/featureFlags/types";
import { useAppSelector } from "./redux";
import { selectReportDocument } from "store/reports/selectors";

export interface ReportViewModeDescription {
    viewMode: ReportViewMode;
    name: string;
    iconName: dgIconNames;
    disabled: boolean;
    tooltip: string | undefined;
}

interface ReportViewModes {
    availableViewModes: ReportViewModeDescription[];
    onViewModeChange: (vm: ReportViewMode) => void;
}

export const useReportViewModes = (documentId: string, workPathType?: WorkPathType): ReportViewModes => {
    const reportDocumentEvaluationStatus = useAppSelector(state => selectReportDocument(state, documentId)?.state.evaluationStatus);
    const navigation = useNavigationService();

    const chatEnabled = useFeatureFlag(FeatureFlags.ChatFeature);

    let views: ReportViewModeDescription[] = [];

    function getViewModeDisabledMessage(view: ReportViewMode): string | null {
        if (view === ReportViewMode.Document && reportDocumentEvaluationStatus === DocumentEvaluationStatus.Confirmed) {
            return 'Your docs are publishing. Please wait until they have finished publishing to access them in the report.'
        }

        // View mode is not disabled, return default null
        return null;
    }

    const getAvailableViewMode = (viewMode: ReportViewMode): ReportViewModeDescription => {
        const disabledMessage = getViewModeDisabledMessage(viewMode);
        return ({
            viewMode,
            iconName: getIconForReportViewMode(viewMode),
            name: getNameForReportViewMode(viewMode),
            disabled: !!disabledMessage,
            tooltip: disabledMessage ?? undefined
        });
    }

    if (workPathType === WorkPathType.Abstract) {
        views.push(getAvailableViewMode(ReportViewMode.Document), getAvailableViewMode(ReportViewMode.Abstract), getAvailableViewMode(ReportViewMode.Table));
    } else {
        views.push(getAvailableViewMode(ReportViewMode.Document), getAvailableViewMode(ReportViewMode.Table));
    }

    if (chatEnabled) {
        views.push(getAvailableViewMode(ReportViewMode.Chat));
    }

    function onViewModeChange(vm: ReportViewMode) {
        navigation.navToReportPage(NavigationType.ReplaceCurrentPage, {
            mode: vm
        });
    }

    return { availableViewModes: views, onViewModeChange };
}
